import {
  LoadingCard,
  SecondaryHeading,
  OutsideClickHandler,
  InputField,
} from "commonUI";
import { useState } from "react";
import { CheckIcon, FormFieldIcon } from "allIcons";
import { isEqual } from "helpers";
import { t } from "i18next";

const optionFilterFn = (child, searchValue) =>
  child.props.value.toString().toLocaleLowerCase().includes(searchValue) ||
  child.props.children.toString().toLocaleLowerCase().includes(searchValue);

export default function SelectField({
  disabled = false,
  icon = "",
  description = "",
  style = {},
  fieldLabel,
  secondaryLabel = "",
  popupLabel = "",
  optionSearchable = false,
  ...props
}) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const getLabel = () => {
    if (Array.isArray(props?.children)) {
      const selectedChild = props?.children?.filter((child) => {
        if (isEqual(child?.props?.value, props?.value)) {
          return child?.props?.children;
        }
        return false;
      })[0]?.props?.children;
      return (selectedChild || fieldLabel) ?? props.placeholder ?? t("common.input.select.placeholder");
    }
    return fieldLabel ?? props.placeholder ?? t("common.input.select.placeholder");
  };

  return (
    <>
      <div
        className={`flex flex-col gap-7 mb-2 ${props.className ?? ""}`}
        style={style}
      >
        {props.label && <h3 className="secondary-heading">{props.label}</h3>}
        {description ? (
          <p className="body-text text-theme-secondary m-0">{description}</p>
        ) : (
          ""
        )}
        <div className="flex w-full">
          {props.isLoading ? (
            <LoadingCard className="h-6" />
          ) : (
            <div
              className="select-field"
              onClick={() => {
                if (disabled) return;
                setOpen((pre) => !pre);
              }}
            >
              <div
                className={`flex items-center justify-between w-full relative ${
                  disabled ? "disabled" : ""
                }`}
              >
                <div className="flex items-center gap-10">
                  {icon}
                  <SecondaryHeading
                    className={`text-13 ${
                      !props.value ? "text-theme-secondary" : ""
                    }`}
                  >
                    {getLabel()}
                  </SecondaryHeading>
                </div>
                <div className="flex items-center">
                  {secondaryLabel ? (
                    <p className="text-13 text-theme-placeholder mr-2">
                      {secondaryLabel}
                    </p>
                  ) : (
                    ""
                  )}
                  <FormFieldIcon fill="#4C5866" width="16" height="9.15" />
                </div>
              </div>
              {
                <OutsideClickHandler
                  onClickOutside={() => {
                    setOpen(false);
                  }}
                  className={`transition-all ${
                    open ? "select-dropdown" : "hide-dropdown"
                  }`}
                >
                  <div>
                    {popupLabel ? (
                      <SecondaryHeading className="mb-3">
                        {popupLabel}
                      </SecondaryHeading>
                    ) : (
                      ""
                    )}
                    {optionSearchable ? (
                      <InputField
                        value={searchValue}
                        setValue={(value) => setSearchValue(value)}
                        placeholder={t("input.search.placeholder")}
                      />
                    ) : (
                      ""
                    )}
                    {props?.children?.length > 0 ? (
                      props.children
                        .filter((child) => optionFilterFn(child, searchValue))
                        .map((child) => (
                          <div
                            className={`select-dropdown-item ${child.props.className}`}
                            onClick={() => {
                              props.setValue(child.props.value);
                              setOpen(false);
                            }}
                          >
                            {child.props.children}
                            <div className="flex items-center gap-8">
                              {child.props.secondaryLabel && (
                                <p className="text-11 text-theme-placeholder">
                                  {child.props.secondaryLabel}
                                </p>
                              )}
                              {isEqual(child.props.value, props.value) && (
                                <CheckIcon fill="#64C1AB" />
                              )}
                            </div>
                          </div>
                        ))
                    ) : (
                      <div className="select-dropdown-item w-full">{t("common.input.select.noData")}</div>
                    )}
                  </div>
                </OutsideClickHandler>
              }
            </div>
          )}
        </div>
      </div>
    </>
  );
}
